import React, { FC } from 'react';
import { Box, Divider, Grid, Stack, SvgIcon, Typography } from '@mui/material';
import styles from './WellnessComponent.module.scss';
import PhysiologicalIcon from '../../../assets/physiological.svg';
import VitalsIcon from '../../../assets/vitals.svg';
import DialComponent from '../DialComponent/DialComponent';
import boddVariables from '../../../variables.module.scss';
import { WellnessItem } from '../../../services/DataFormatService';
import SliderComponent from '../SliderComponent/SliderComponent';
import BloodPressureComponent from '../BloodPressureComponent/BloodPressureComponent';
import GeneralWellnessComponent from '../GeneralWellnessComponent/GeneralWellnessComponent';
import MentalStressComponent from '../MentalStressComponent/MentalStressComponent';
import FacialAgeComponent from '../FacialAgeComponent/FacialAgeComponent';
import NextStepsComponent from '../NextStepsComponent/NextStepsComponent';

interface WellnessComponentProps {
    wellnessItems: { [key: string]: WellnessItem },
}

const HeadingUnderline: React.FC = () => (
    <Divider sx={{ borderColor: boddVariables.black, backgroundColor: 'transparent', pt: 1.25, mb: 3 }} />
);

const WellnessComponent: FC<WellnessComponentProps> = (props) => {
    const { wellnessItems } = props;

    const heartRateItem = wellnessItems.heartRate;
    const breathingItem = wellnessItems.breathing;
    const systolicBPItem = wellnessItems.systolicBP;
    const diastolicBPItem = wellnessItems.diastolicBP;

    const heartRateVariabilityItem = wellnessItems.heartRateVariability;
    const cardiacWorkloadItem = wellnessItems.cardiacWorkload;
    const vascularCapacityItem = wellnessItems.vascularCapacity;

    const mentalStressItem = wellnessItems.mentalStressIndex;
    const facialAgeItem = wellnessItems.facialAge;

    const vitalScoreItem = wellnessItems.vitalScore;
    const physicalScoreItem = wellnessItems.physicalScore;
    const physiologicalScoreItem = wellnessItems.physiologicalScore;
    const wellnessScoreItem = wellnessItems.wellnessScore;

    return (
        Object.keys(wellnessItems).length > 0 ? (
            <div className={styles.WellnessComponent} data-testid="WellnessComponent">
                <Typography
                    variant="h1"
                    sx={{ pb: 4 }}
                >
                    Health Vitals
                </Typography>
                {(heartRateItem || breathingItem || (systolicBPItem && diastolicBPItem)) && (
                    <Box>
                        <Stack direction="row" alignItems="center">
                            <SvgIcon sx={{ height: 24, width: 24 }}><VitalsIcon /></SvgIcon>
                            <Typography
                                sx={{
                                    ml: 1.25,
                                    fontFamily: 'Poppins',
                                    fontWeight: '600',
                                    fontSize: '24px',
                                    lineHeight: '36px',
                                }}
                            >
                                Vitals
                            </Typography>
                        </Stack>
                        <HeadingUnderline />
                        <Grid container>
                            {heartRateItem && (
                                <Grid item xs={6} md={4}>
                                    <DialComponent wellnessItem={heartRateItem} />
                                </Grid>
                            )}
                            {breathingItem && (
                                <Grid item xs={6} md={4}>
                                    <DialComponent wellnessItem={breathingItem} />
                                </Grid>
                            )}
                        </Grid>
                        {systolicBPItem && diastolicBPItem
                            && (
                                <BloodPressureComponent
                                    systolicBPItem={systolicBPItem}
                                    diastolicBPItem={diastolicBPItem}
                                />
                            )}
                    </Box>
                )}
                {(heartRateVariabilityItem || cardiacWorkloadItem || vascularCapacityItem) && (
                    <Box sx={{ my: 5 }}>
                        <Stack direction="row" alignItems="center">
                            <SvgIcon sx={{ height: 24, width: 24 }}><PhysiologicalIcon /></SvgIcon>
                            <Typography
                                sx={{
                                    ml: 1.25,
                                    fontFamily: 'Poppins',
                                    fontWeight: '600',
                                    fontSize: '24px',
                                    lineHeight: '36px',
                                }}
                            >
                                Physiological
                            </Typography>
                        </Stack>
                        <HeadingUnderline />
                        <Grid container>
                            {heartRateVariabilityItem && (
                                <Grid item xs={6} md={4}>
                                    <DialComponent
                                        wellnessItem={heartRateVariabilityItem}
                                    />
                                </Grid>
                            )}
                            {cardiacWorkloadItem && (
                                <Grid item xs={6} md={4}>
                                    <DialComponent
                                        wellnessItem={cardiacWorkloadItem}
                                    />
                                </Grid>
                            )}
                            {vascularCapacityItem && (
                                <Grid item xs={6} md={4}>
                                    <DialComponent
                                        wellnessItem={vascularCapacityItem}
                                    />
                                </Grid>
                            )}
                        </Grid>
                    </Box>
                )}
                <Box sx={{ my: 5 }}>
                    <Typography
                        sx={{
                            textAlign: 'left',
                            fontFamily: 'Poppins',
                            fontWeight: '600',
                            fontSize: '24px',
                            lineHeight: '36px',
                        }}
                    >
                        Overall
                    </Typography>
                    <HeadingUnderline />
                    {(mentalStressItem || facialAgeItem) && (
                        <Grid container spacing={2.5} alignItems="stretch" sx={{ pb: 3 }}>
                            {mentalStressItem && (
                                <Grid item xs>
                                    <MentalStressComponent mentalStressItem={mentalStressItem} />
                                </Grid>
                            )}
                            {facialAgeItem && (
                                <Grid item xs>
                                    <FacialAgeComponent facialAgeItem={facialAgeItem} />
                                </Grid>
                            )}
                        </Grid>
                    )}
                    {vitalScoreItem && (<SliderComponent wellnessItem={vitalScoreItem} />)}
                    {physiologicalScoreItem && (<SliderComponent wellnessItem={physiologicalScoreItem} />)}
                    {physicalScoreItem && (<SliderComponent wellnessItem={physicalScoreItem} />)}
                    {wellnessScoreItem && (<GeneralWellnessComponent wellnessScoreItem={wellnessScoreItem} />)}
                </Box>
                <NextStepsComponent />
            </div>
        ) : null
    );
};

export default WellnessComponent;
